/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable prefer-const */
import { ChangeDetectionStrategy, Component, OnInit, OnDestroy, input, output, inject } from '@angular/core';
import { AbstractControl, FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { StyleClassModule } from 'primeng/styleclass';
import { ButtonModule } from 'primeng/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AsyncPipe } from '@angular/common';
import { PopoverModule } from 'primeng/popover';
import { AvatarModule } from 'primeng/avatar';

import { ToastMessageService } from '@fitness-force/api';
import { AppErrors } from '@fitness-force/models';
import { ErrorMsgComponent } from '@fitness-force/errors';;
import type { ClientSubscriptionFreezeResponse, StopClientSubscriptionFreezeRequest } from '@fitness-force/models';
import { FreezeService } from '@fitness-force/api';
import { COMPANY_INFO } from '@fitness-force/state';
import { environment } from '@fitness-force/environments';
import { ErrorsComponent,  } from '@fitness-force/errors';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { Store } from '@ngrx/store';
import { FreezeReasonService } from '../../services/freeze-reason.service';
import type { FreezeCycleAdjustmentResponse, CalculateFreezeAdjustmentRequest } from '../../models/freeze-reason.model';
import { RecurringTemplateAdjustmentType } from '../../enums/freeze.enum';

@Component({
  selector: 'freeze-stop',
  templateUrl: './freeze-stop.component.html',
  styleUrls: ['./freeze-stop.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    DatePickerModule,
    SelectModule,
    StyleClassModule,
    ButtonModule,
    PopoverModule,
    AvatarModule,
    ErrorMsgComponent,
    ErrorsComponent,
    TranslateModule,
    AsyncPipe
]
})
export class FreezeStopComponent implements OnInit, OnDestroy {
  private freezeService = inject(FreezeService);
  private freezeReasonService = inject(FreezeReasonService);
  private translate = inject(TranslateService);
  private store = inject(Store);
  private fb = inject(UntypedFormBuilder);
  private toastMessageService = inject(ToastMessageService);
  private readonly destroy$ = new Subject<void>();

  readonly visible = input(false);
  readonly freeze$ = input<BehaviorSubject<ClientSubscriptionFreezeResponse | null>>(new BehaviorSubject<ClientSubscriptionFreezeResponse | null>(null), { alias: "freeze" });
  readonly freezeId = input<number>(0);
  readonly clientId = input<number>(0);
  readonly subscriptionId = input<number>(0);
  readonly tenantId = input<number>(0);

  readonly hidePopup = output<boolean>({ alias: 'hidePopup' });

  fg: UntypedFormGroup;
  isUpdated$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  freezeCycleAdjustment$ = new BehaviorSubject<FreezeCycleAdjustmentResponse | null>(null);
  freezeCycleAdjustmentLoading$ = new BehaviorSubject<boolean>(false);

  //#region error popup
  error = new BehaviorSubject(null as AppErrors | null);
  showErrorPopup = new BehaviorSubject(false);
  errorMessages = [] as any;

  btnLoading = new BehaviorSubject(false);
  //#endregion error popup

  constructor() {
    this.fg = this.fb.group({
      reason: new UntypedFormControl('', [Validators.required]),
    });
  }

  ngOnInit(): void {
    this.componentInit();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  componentInit() {
    this.store.select(COMPANY_INFO).subscribe((responseData) => {
      this.translate.setDefaultLang(responseData?.selectedLang?.LANG_CODE);
      this.translate.use(responseData?.selectedLang?.LANG_CODE);
    });

    setTimeout(() => {
      this.calculateFreezeCycleAdjustment();
    }, 300);
  }

  calculateFreezeCycleAdjustment(): void {
    const freeze = this.freeze$()?.value;
    const tenantId = this.tenantId() || freeze?.tenant_id || 0;
    const clientId = this.clientId() || freeze?.client_id || 0;
    const subscriptionId = this.subscriptionId() || freeze?.client_subscription?.id || 0;
    const freezeId = this.freezeId() || freeze?.freeze_id || 0;

    if (!tenantId || !clientId || !subscriptionId || !freezeId) return;

    const startDate = freeze?.start_date;
    const endDate = freeze?.end_date;

    if (!startDate || !endDate) return;

    const payload: CalculateFreezeAdjustmentRequest = {
      requested_start_date: typeof startDate === 'string' ? startDate : (startDate as Date).toISOString(),
      requested_end_date: typeof endDate === 'string' ? endDate : (endDate as Date).toISOString(),
      adjustment_type: RecurringTemplateAdjustmentType.FreezeStop,
      freeze_id: freezeId,
      stop_date: new Date().toISOString(),
    };

    this.freezeCycleAdjustmentLoading$.next(true);

    this.freezeReasonService
      .calculateFreezeAdjustment(tenantId, clientId, subscriptionId, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: FreezeCycleAdjustmentResponse) => {
          this.freezeCycleAdjustment$.next(response);
          this.freezeCycleAdjustmentLoading$.next(false);
        },
        error: () => {
          this.freezeCycleAdjustment$.next(null);
          this.freezeCycleAdjustmentLoading$.next(false);
        },
      });
  }

  get ReasonControl(): AbstractControl | null {
    return this.fg.controls['reason'] as AbstractControl;
  }

  HidePopup() {
    this.hidePopup.emit(this.isUpdated$.value);
    this.hidePopup.emit(this.isUpdated$.value);
  }

  StopFreeze(): void {
    this.btnLoading.next(true);
    this.fg.markAllAsTouched();
    if (this.fg.valid) {
      let param = {} as StopClientSubscriptionFreezeRequest;
      const currentDate = new Date();
      param.freeze_stop_date = currentDate.toISOString(); //new Date(Date.UTC(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(),currentDate.getHours(),currentDate.getMinutes(),currentDate.getSeconds())).toISOString();
      param.stop_reason = this.ReasonControl?.value;
      param.calculated_freeze_cycle_adjustment_info_and_adjusted_future_cycle =
        this.freezeCycleAdjustment$.value ?? null;

      this.freezeService.PostStopFreeze(
        environment.BASE_URL,
        this.tenantId(),
        this.clientId(),
        this.subscriptionId(),
        this.freezeId(),
        param
      ).subscribe({
        next: (res) => {
          if (res) {
            this.isUpdated$.next(true);
            this.translate.get('FREEZE.FREEZE_STOP.REQUEST_SUCCESS').subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
            });
            this.btnLoading.next(false);
            setTimeout(() => { this.HidePopup(); }, 1000);
          }
          this.btnLoading.next(false);
        },
        error: (err) => {
          this.error.next(err);
          this.showErrorPopup.next(true);
          this.btnLoading.next(false);
        }
      });
    }
    else{
      this.btnLoading.next(false);
    }
  }

  HideErrorPopup(event: any) {
    this.showErrorPopup?.next(false);
    this.errorMessages = [];
    this.error.next(null);
  }
}
